import React, {useState} from 'react';
import {PhasePill} from './PhasePill';
import {complexityMeta, hasStrategyContext, solutionList} from './scoring';
import {updatedStamp} from '../../lib/time';
import type {Domain} from './types';

/** Full-screen modal detailing one domain's JTBDs. Ported verbatim from the prototype. */
export function DomainDetail({domain, onClose}: {domain: Domain; onClose: () => void}): React.ReactElement {
  const [expandedJtbd, setExpandedJtbd] = useState<number | null>(null);
  const tools = [...new Set(domain.jtbds.flatMap(j => solutionList(j)).filter(Boolean))];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.5)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 14,
          width: '100%',
          maxWidth: 780,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 80px rgba(0,0,0,0.2)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px 16px',
            borderBottom: '1px solid #F1F5F9',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexShrink: 0
          }}
        >
          <div>
            <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4}}>
              <h2 style={{fontSize: 18, fontWeight: 700, color: '#0F172A'}}>{domain.name}</h2>
            </div>
            <div style={{fontSize: 12, color: '#64748B'}}>
              {domain.leader} · {domain.area} · {domain.jtbds.length} JTBDs
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#F8FAFC',
              border: '1px solid #E2E8F0',
              color: '#64748B',
              borderRadius: 8,
              padding: '6px 14px',
              cursor: 'pointer',
              fontFamily: "'DM Mono', monospace",
              fontSize: 12,
              flexShrink: 0
            }}
          >
            ✕ Close
          </button>
        </div>

        {/* Body */}
        <div style={{overflowY: 'auto', padding: '16px 24px 24px'}}>
          {/* Tools in play */}
          {tools.length > 0 && (
            <div style={{marginBottom: 16, padding: '10px 14px', background: '#F8FAFC', borderRadius: 8}}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#94A3B8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: 8
                }}
              >
                Tools &amp; Vendors in Play
              </div>
              <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                {tools.map(t => (
                  <span
                    key={t}
                    style={{
                      fontSize: 11,
                      padding: '3px 8px',
                      background: '#fff',
                      border: '1px solid #E2E8F0',
                      borderRadius: 6,
                      color: '#475569',
                      fontWeight: 500
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Provisional-phase banner */}
          {domain.jtbds.some(j => j.phaseProvisional) && (
            <div
              style={{
                marginBottom: 12,
                padding: '8px 12px',
                background: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: 8,
                fontSize: 11,
                color: '#B45309',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                lineHeight: 1.5
              }}
            >
              <span style={{fontSize: 12, flexShrink: 0}}>⚑</span> Phases shown are provisional defaults from the initial baseline — pending current-state confirmation from the domain leader.
            </div>
          )}

          {/* JTBDs */}
          {domain.jtbds.map((j, i) => {
            const updatedLabel = updatedStamp(j.lastUpdated);
            const expanded = expandedJtbd === i;
            const sc = j.strategyContext;
            const hasSC = hasStrategyContext(j);
            return (
              <div key={i} style={{marginBottom: 6, border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden'}}>
                <div
                  onClick={() => setExpandedJtbd(expanded ? null : i)}
                  style={{display: 'flex', alignItems: 'center', padding: '12px 16px', cursor: 'pointer', gap: 12}}
                  onMouseEnter={e => (e.currentTarget.style.background = '#F8FAFC')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >
                  <PhasePill phase={j.phase} small />
                  <div style={{flex: 1, minWidth: 0}}>
                    <span style={{fontSize: 13, fontWeight: 500, color: '#0F172A'}}>{j.name}</span>
                    {j.needsFollowUp && (
                      <div style={{display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, fontSize: 10, fontWeight: 700, color: '#B45309'}}>
                        <span style={{fontSize: 11}}>⚑</span> Deferred — needs Domain/SA leader follow-up
                      </div>
                    )}
                  </div>
                  <div style={{display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0}}>
                    {solutionList(j).map(s => (
                      <span
                        key={s}
                        style={{fontSize: 10, padding: '1px 6px', background: '#F1F5F9', borderRadius: 4, color: '#475569', fontWeight: 500}}
                      >
                        {s}
                      </span>
                    ))}
                    <span style={{fontSize: 10, color: '#94A3B8', fontFamily: "'DM Mono', monospace", whiteSpace: 'nowrap'}}>{updatedLabel}</span>
                    <span style={{fontSize: 12, color: '#CBD5E1'}}>{expanded ? '▲' : '▼'}</span>
                  </div>
                </div>
                {expanded && (
                  <div style={{padding: '0 16px 14px', borderTop: '1px solid #F1F5F9'}}>
                    {j.notes && (
                      <p style={{fontSize: 12, color: '#475569', lineHeight: 1.6, marginBottom: hasSC ? 12 : 0, marginTop: 10}}>{j.notes}</p>
                    )}
                    {/* Next Step & Timing */}
                    {(() => {
                      const hasStep = !!j.nextStep;
                      const hasTiming = !!j.nextStepTiming;
                      const state = hasStep && hasTiming ? 'complete' : hasStep ? 'partial' : 'missing';
                      const stateStyle = {
                        complete: {bg: '#F0FDF4', border: '#BBF7D0', accent: '#16A34A', badge: null as string | null},
                        partial: {bg: '#FFFBEB', border: '#FDE68A', accent: '#D97706', badge: 'Needs Timing'},
                        missing: {bg: '#FEF2F2', border: '#FECACA', accent: '#DC2626', badge: 'Missing'}
                      }[state];
                      return (
                        <div
                          style={{
                            background: stateStyle.bg,
                            borderRadius: 8,
                            padding: '10px 12px',
                            marginTop: 10,
                            marginBottom: hasSC ? 10 : 0,
                            border: `1px solid ${stateStyle.border}`
                          }}
                        >
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: hasStep ? 6 : 0}}>
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: stateStyle.accent,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em'
                              }}
                            >
                              Next Step &amp; Timing
                            </div>
                            {stateStyle.badge && (
                              <span
                                style={{
                                  fontSize: 9,
                                  fontWeight: 700,
                                  padding: '1px 6px',
                                  borderRadius: 4,
                                  background: stateStyle.bg,
                                  color: stateStyle.accent,
                                  border: `1px solid ${stateStyle.border}`
                                }}
                              >
                                {stateStyle.badge}
                              </span>
                            )}
                          </div>
                          {hasStep ? (
                            <div>
                              <div style={{fontSize: 12, color: '#334155', lineHeight: 1.5}}>{j.nextStep}</div>
                              {hasTiming ? (
                                <div style={{fontSize: 11, color: '#16A34A', fontWeight: 600, marginTop: 4}}>{j.nextStepTiming}</div>
                              ) : (
                                <div style={{fontSize: 10, color: '#D97706', marginTop: 4, fontStyle: 'italic'}}>Timing not yet defined</div>
                              )}
                            </div>
                          ) : (
                            <div style={{fontSize: 11, color: '#94A3B8', marginTop: 4}}>
                              No next step defined — needs leader input in next update cycle
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {hasSC && sc && (
                      <div style={{background: '#F8FAFC', borderRadius: 8, padding: '10px 12px'}}>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#94A3B8',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            marginBottom: 10
                          }}
                        >
                          Strategy Context
                        </div>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 16px'}}>
                          {(
                            [
                              ['Human Capital', sc.humanCapitalImpact],
                              ['Tech Readiness', sc.techReadiness],
                              ['Funding', sc.fundingStatus],
                              ['AI vs Auto', sc.aiVsAutomation],
                              ['Synergy', sc.synergy],
                              ['Manual Effort', sc.manualEffort],
                              ['Leader Opportunity', sc.leaderOpportunity],
                              ['Approach', sc.approach],
                              ['Complexity', sc.complexity ? `${sc.complexity} — ${complexityMeta(sc.complexity).label}` : null]
                            ] as [string, string | null | undefined][]
                          ).map(
                            ([label, val]) =>
                              val && (
                                <div key={label}>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      color: '#94A3B8',
                                      fontWeight: 600,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.08em',
                                      marginBottom: 2
                                    }}
                                  >
                                    {label}
                                  </div>
                                  <div style={{fontSize: 11, fontWeight: 600, color: '#334155'}}>{val}</div>
                                </div>
                              )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
